import { useEffect, useState } from "react";
import {
    Cake,
    CalendarDays,
    Search,
} from "lucide-react";
import { getUpcomingBirthdays } from "@/services/masters";
import Pagination from "../common/Pagination";

interface Birthday {
    name: string;
    username: string;
    dob: string;
    days_left: number;
    status:string
}

interface BirthdayPage {
    count: number;
    total_pages: number;
    next: string | null;
    previous: string | null;
    results: Birthday[];
}

interface BirthdayResponse {
    Today: BirthdayPage;
    Tomorrow: BirthdayPage;
    Upcoming: BirthdayPage;
}

const ITEMS_PER_PAGE = 20;

type Tab = "Today" | "Tomorrow" | "Upcoming";

export default function UpcomingBirthdays() {
    const [birthdays, setBirthdays] =
        useState<BirthdayResponse | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [activeTab, setActiveTab] =
        useState<Tab>("Today");

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] =
        useState("");

    const [pages, setPages] = useState({
        Today: 1,
        Tomorrow: 1,
        Upcoming: 1,
    });

    const loadUpcomingBirthdays = async (
        currentPages = pages,
        searchValue = debouncedSearch
    ) => {
        try {
            setLoading(true);
            setError(null);

            const response = await getUpcomingBirthdays(
                ITEMS_PER_PAGE,
                currentPages.Today,
                currentPages.Tomorrow,
                currentPages.Upcoming,
                searchValue
            );

            const data =
                response.data as BirthdayResponse;

            setBirthdays(data);
        } catch (err) {
            console.error(
                "Failed to load birthdays:",
                err
            );

            setError(
                "Unable to load upcoming birthdays."
            );

            setBirthdays(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());

            setPages({
                Today: 1,
                Tomorrow: 1,
                Upcoming: 1,
            });
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        loadUpcomingBirthdays(
            pages,
            debouncedSearch
        );
    }, [pages, debouncedSearch]);

    if (loading && !birthdays) {
        return (
            <div className="flex h-80 items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />

                    <p className="text-sm text-gray-500">
                        Loading upcoming birthdays...
                    </p>
                </div>
            </div>
        );
    }

    if (error && !birthdays) {
        return (
            <div className="flex h-80 items-center justify-center">
                <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
                    <h3 className="text-lg font-semibold text-red-600">
                        Something went wrong
                    </h3>

                    <p className="mt-2 text-gray-600">
                        {error}
                    </p>

                    <button
                        onClick={() =>
                            loadUpcomingBirthdays()
                        }
                        className="mt-4 rounded-lg bg-red-600 px-5 py-2 text-white transition hover:bg-red-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!birthdays) {
        return (
            <div className="flex h-80 items-center justify-center rounded-xl border border-dashed border-gray-300">
                <p className="text-gray-500">
                    No birthday data available.
                </p>
            </div>
        );
    }

    const sections: Record<
        Tab,
        {
            color: string;
            data: BirthdayPage;
        }
    > = {
        Today: {
            color: "from-pink-500 to-rose-500",
            data: birthdays.Today,
        },

        Tomorrow: {
            color: "from-blue-500 to-cyan-500",
            data: birthdays.Tomorrow,
        },

        Upcoming: {
            color: "from-violet-500 to-purple-600",
            data: birthdays.Upcoming,
        },
    };

    const currentSection =
        sections[activeTab];

    const currentList =
        currentSection.data.results;

    const handleTabChange = (tab: Tab) => {
        setActiveTab(tab);
    };

    const handlePageChange = (page: number) => {
        setPages((prev) => ({
            ...prev,
            [activeTab]: page,
        }));
    };

    return (
        <div className="space-y-8 p-6">
            <div>
                <h2 className="flex items-center gap-2 text-3xl font-bold text-gray-800">
                    <Cake className="h-8 w-8 text-pink-500" />
                    Upcoming Birthdays
                </h2>

                <p className="mt-1 text-gray-500">
                    Celebrate birthdays 🎉
                </p>
            </div>

            <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                <input
                    type="text"
                    value={search}
                    onChange={(e) =>
                        setSearch(e.target.value)
                    }
                    placeholder="Search birthdays..."
                    className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
            </div>

            <div className="flex w-full rounded-2xl border border-gray-200 bg-gray-50 p-1 shadow-sm">
                {(
                    [
                        "Today",
                        "Tomorrow",
                        "Upcoming",
                    ] as const
                ).map((tab) => {
                    const count =
                        sections[tab].data.count;

                    return (
                        <button
                            key={tab}
                            onClick={() =>
                                handleTabChange(tab)
                            }
                            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold transition-all duration-300 ${
                                activeTab === tab
                                    ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-md"
                                    : "text-gray-600 hover:bg-white hover:text-gray-900"
                            }`}
                        >
                            <CalendarDays size={18} />

                            <span>{tab}</span>

                            <span
                                className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                                    activeTab === tab
                                        ? "bg-white/20 text-white"
                                        : "bg-indigo-100 text-indigo-700"
                                }`}
                            >
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl">
                <div
                    className={`bg-gradient-to-r ${currentSection.color} px-6 py-4 text-white`}
                >
                    <div className="flex items-center gap-3">
                        <CalendarDays size={24} />

                        <h3 className="text-xl font-semibold">
                            {activeTab}
                        </h3>
                    </div>
                </div>

                <div className="p-6">
                    <BirthdayGroup
                        title={activeTab}
                        icon={<Cake size={20} />}
                        list={currentList}
                        badgeColor="bg-pink-100 text-pink-700"
                    />

                    {currentSection.data
                        .total_pages > 1 && (
                        <div className="mt-6">
                            <Pagination
                                itemsPerPage={
                                    ITEMS_PER_PAGE
                                }
                                currentPage={
                                    pages[activeTab]
                                }
                                totalPages={
                                    currentSection.data
                                        .total_pages
                                }
                                totalCount={
                                    currentSection.data
                                        .count
                                }
                                onPageChange={
                                    handlePageChange
                                }
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function BirthdayGroup({
    title,
    icon,
    list,
    badgeColor,
}: {
    title: string;
    icon: React.ReactNode;
    list: Birthday[];
    badgeColor: string;
}) {
    return (
        <div>

            {list.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 py-8 text-center text-gray-400">
                    No birthdays 🎉
                </div>
            ) : (
                <div className="space-y-4">
                    {list.map((person, index) => (
                        <div
                            key={`${person.username}-${person.dob}-${index}`}
                            className="group flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50 p-4 transition hover:-translate-y-1 hover:border-pink-200 hover:bg-white hover:shadow-lg"
                        >
                            <div className="flex items-center gap-4">
                                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-pink-500 to-orange-400 text-lg font-bold text-white">
                                    {person.name.charAt(
                                        0
                                    )}
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-800">
                                        {person.name}{" "}
                                        {person.username}
                                    </h4>

                                    <p className="text-sm text-gray-500">
                                        🎂 {person.dob}
                                    </p>
                                </div>
                            </div>

                            <div className="text-right flex flex-row gap-2">
                                 <h4 className="font-semibold text-gray-800 bg-amber-100 rounded-full px-3 py-1 text-xs">
                                        {person.status}
                                    </h4>
                                <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                                    {person.days_left ===
                                    0
                                        ? "Today"
                                        : `${person.days_left} days left`}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
